import React from 'react';
import { LucideIcon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface StatCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: LucideIcon;
  bgColor: string;
  borderColor: string;
  subtitleColor?: string;
  lightBgColor?: string;
  lightBorderColor?: string;
  lightTitleColor?: string;
  lightValueColor?: string;
  lightSubtitleColor?: string;
  lightIconWrapper?: string;
  lightIconColor?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  icon: Icon,
  bgColor,
  borderColor,
  subtitleColor = 'text-gray-400',
  lightBgColor,
  lightBorderColor,
  lightTitleColor,
  lightValueColor,
  lightSubtitleColor,
  lightIconWrapper,
  lightIconColor
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const containerClasses = `${
    isDark
      ? `${bgColor} ${borderColor}`
      : `${lightBgColor ?? 'bg-white'} ${lightBorderColor ?? 'border-teal-200'} shadow-lg shadow-teal-100/40`
  } border-l-4 rounded-xl p-6 transition-all duration-300`;

  const resolvedTitleColor = isDark ? 'text-slate-100' : lightTitleColor ?? 'text-slate-600';
  const resolvedValueColor = isDark ? 'text-white' : lightValueColor ?? 'text-teal-700';
  const resolvedSubtitleColor = isDark ? subtitleColor : lightSubtitleColor ?? 'text-slate-500';
  const iconWrapperClasses = isDark
    ? 'p-3 bg-white/10 rounded-full'
    : lightIconWrapper ?? 'p-3 bg-teal-100 rounded-full';
  const iconColor = isDark ? 'text-white/80' : lightIconColor ?? 'text-teal-600';

  return (
    <div className={containerClasses}>
      <div className="flex items-center justify-between">
        <div>
          <p className={`${resolvedTitleColor} text-sm mb-2 font-medium`}>{title}</p>
          <p className={`text-3xl font-bold ${resolvedValueColor} mb-1`}>{value}</p>
          {subtitle && (
            <p className={`text-sm ${resolvedSubtitleColor} font-medium`}>{subtitle}</p>
          )}
        </div>
        <div className={iconWrapperClasses}>
          <Icon className={iconColor} size={24} />
        </div>
      </div>
    </div>
  );
};

export default StatCard;